import InfoIcon from '@mui/icons-material/Info'
import { Alert, Box, Divider, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useRef } from 'react'

import MessageBubble from './MessageBubble'

const MESSAGE_LIMIT = 50

/**
 * MessageList - Displays a list of chat messages with auto-scroll
 *
 * @param {Array} messages - Array of message objects
 * @param {string} currentUserId - ID of the current user
 * @param {Object} sx - Additional styles to apply
 */
export default function MessageList({ messages = [], currentUserId, sx = {} }) {
  const messagesEndRef = useRef(null)
  const containerRef = useRef(null)

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior })
  }

  useEffect(() => {
    scrollToBottom('auto')
  }, [])

  useEffect(() => {
    scrollToBottom('smooth')
  }, [messages.length])

  const groupMessagesByDate = (messages) => {
    const groups = {}
    messages.forEach((msg) => {
      const dateKey = dayjs(msg.timestamp).format('YYYY-MM-DD')
      if (!groups[dateKey]) {
        groups[dateKey] = []
      }
      groups[dateKey].push(msg)
    })
    return groups
  }

  const getDateLabel = (dateString) => {
    const msgDate = dayjs(dateString)
    const today = dayjs()
    const yesterday = dayjs().subtract(1, 'day')

    if (msgDate.isSame(today, 'day')) {
      return 'Today'
    } else if (msgDate.isSame(yesterday, 'day')) {
      return 'Yesterday'
    } else {
      return msgDate.format('MMMM D, YYYY')
    }
  }

  const messageGroups = groupMessagesByDate(messages)

  return (
    <Box
      ref={containerRef}
      sx={{
        flexGrow: 1,
        overflowY: 'auto',
        px: 2,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      {messages.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No messages yet. Start a conversation!
          </Typography>
        </Box>
      ) : (
        <>
          {messages.length >= MESSAGE_LIMIT && (
            <Alert
              severity="info"
              icon={<InfoIcon />}
              sx={{
                mb: 2,
                fontSize: '0.75rem',
                '& .MuiAlert-message': {
                  fontSize: '0.75rem',
                },
              }}
            >
              We're showing you the most recent {MESSAGE_LIMIT} messages. Older messages aren't loaded to keep the chat fast and responsive.
            </Alert>
          )}
          {Object.entries(messageGroups).map(([dateKey, msgs]) => (
            <Box key={dateKey}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', px: 1 }}>
                  {getDateLabel(dateKey)}
                </Typography>
              </Divider>
              {msgs.map((message, index) => (
                <MessageBubble key={message.id || index} message={message} isCurrentUser={message.sender?.id === currentUserId || message.sender?.resourceId === currentUserId} />
              ))}
            </Box>
          ))}
        </>
      )}
      <div ref={messagesEndRef} />
    </Box>
  )
}
